import type { MouseController } from 'app/chrome/mouse/MouseController';
import type { PreloadController } from 'app/chrome/preload/PreloadController';
import type { WorkController } from 'app/views/work/WorkController';
import type { WorkTilePixi } from 'app/module/work/WorkTilePixi';

/**
 * Typed cross-module scratch state — the former ad-hoc `(window as any).X`
 * globals, moved off `window` into one documented module. Same mutable,
 * last-writer-wins semantics as before; only the storage location changed.
 *
 * Defaults preserve the old first-read behaviour (window props started out
 * `undefined`): flags start `false`, refs `null`, and numbers that used to be
 * read before their first write start `undefined` (so arithmetic still yields
 * NaN rather than silently becoming 0-based).
 *
 * NOT here (still real window globals, declared in src/types/globals.d.ts):
 * the Blade page contract (projectData, mode, isHome, ...) and the hot
 * per-frame channels (deltaTime, appIsPaused). Viewport size caching lives
 * on the utils/device singleton (device.innerWidth/innerHeight).
 */
export const globals = {
	// --- Pointer / input state -------------------------------------------------

	// True while the most recent pointer coords came from a touch event.
	// Written by MouseTracking (+ device.cordsFromTouch mirror); read all over
	// (tiles, cursor view, drag, particles) to branch touch vs mouse behaviour.
	lastCordsFromTouch: false,
	// Finger currently down. Written by MouseTracking + HomeController;
	// read by TimelineRegion / ParticlePath.
	touchActive: false,
	// Legacy home-intro repell flag read by itemMotion (TimelineItemRepell/
	// Path/Attract). NOTE: no writer exists in the codebase today — always false.
	introActive: false,
	// Legacy button-hover repell flag for the same itemMotion readers.
	// Only writer today is HomeTimeline (sets it false).
	buttonActive: false,
	// Set true by HomeController when the opening "action" is clicked; read by
	// AppController/HomeController to skip the intro path.
	openingActionClicked: false,

	// --- Cross-controller handles ---------------------------------------------

	// The MouseController instance (set in its constructor); read by
	// InertiaDrag for the current move axis.
	mouseController: null as MouseController,
	// The PreloadController instance (set in its constructor); read by
	// HomeTimeline to report preload progress milestones.
	preloadController: null as PreloadController,
	// The WorkController that owns the currently-open case study; set on
	// project open, nulled on close. Read by ProjectController + WorkController.
	activeWorkController: null as WorkController,
	// The work tile the open case study was launched from (WorkScene.activeTile
	// snapshot). Written by WorkController; read by ProjectController for
	// scroll/resize/tick interop during header scroll.
	activeTile: null as WorkTilePixi,

	// --- Perf / app-mode flags --------------------------------------------------

	// One frame of "skip the normal particle update" after a layout switch.
	// Written by HomeField + field processors; consumed (and reset) by
	// ParticleBehaviourLookup / HomeParticle(Manager).
	skipNormalParticleUpdates: false,
	// Slow-frame-rate mode (PixiController's FPS watchdog). Written by
	// PixiController; read by SpacesDepartment to skip the reflection filter.
	slowFpsMode: false,
	// True while the slow-FPS dialog is on screen. Written by PixiController;
	// read by WorkController to pause interactions.
	slowFpsShowing: false,
	// True when the preloader is the circle variant. Written by LoaderGraphic;
	// read by HomeController.
	inCircleLoader: false,
	// True while the work section is animating out. Written by HomeController;
	// read by MouseCursorView. NOTE: only writer today sets it false.
	hidingWork: false,
	// Legacy scaffolding flag: only ever assigned `false` at boot (src/index.ts
	// used to), so every `if (globals.tmpApp)` guard is currently dead-false.
	tmpApp: false,

	// --- Home scene tuning (written by home processors, read by grid/particles) --

	// Systems-lines mode: draw one long line per pass instead of many short ones.
	// Written by SystemsDepartment; read by SingleLine.
	singleLineMode: false,
	// Systems-lines mode: constrain line direction changes to right angles.
	// Written by SystemsDepartment; read by SingleLine.
	rightAngleMode: false,
	// Speed multiplier for systems lines (tweened by SystemsDepartment;
	// read by SingleLine). Undefined until the systems scene first sets it.
	fastModeScale: undefined as number,
	// Current accent colour (hex string) for systems lines/particles.
	// Written by SystemsDepartment; read by SingleLine.
	accentColour: undefined as string,
	// Screens-scene colour phase, toggled 'yellow' <-> 'green' by
	// ScreensDepartment; read by RadialHalftoneImage.
	colourPhase: undefined as string,
	// Colour set the screens bounce particles tint towards. Written by
	// ScreensDepartment; read by ParticlePhysics.
	bounceColourTarget: null as string[],
	// Scale of the screens content zone (ScreensDepartment writes;
	// RadialHalftoneImage reads). Undefined until the screens scene lays out.
	screensZoneScale: undefined as number,
	// True when the screens scene uses its mobile layout (< mobile breakpoint).
	// Written by ScreensDepartment; read by HomeMicroShape.
	screensMobileLayout: false,

	// --- Home tuning scalars ---------------------------------------------------
	// (The former virtualWidth/virtualHeight/virtualYRatio globals died in the
	// size-cache consolidation — swap-side readers use sceneProps.sizes
	// (container.w/h = virtual width/height), maintained by HomeTimeline.)

	// Viewport-width-based particle scale multiplier (0.5-1 at 0-1920px).
	// Written by HomeController.resize; read by ParticleProperties.
	scaleFactorM: undefined as number,
	// Y offset of the home particle grid container. Written by
	// ParticleGridTargets; read by ParticleTrailEmitter.
	yGrid: undefined as number,

	// --- Home Pixi scene refs (created once per home boot) ----------------------
	// Runtime objects are Pixi v8 (CDN); the type below is the ambient v5-based
	// `PIXI` namespace this codebase compiles against (see src/types/globals.d.ts).
	// The particles-internal refs (icon/systems/spacesOutter containers, spaces
	// mask, screens mask image, texture dictionary, home field) live on
	// ParticlesStage / ParticlesSceneProps now — only this seam-crossing ref stays.

	// The screens frame sprite (HomeTimeline writes — shared/timeline side;
	// ScreensDepartment — swap side — reads position/scale to align the stage's
	// screens-mask-image sprite). Crosses the timeline<->particles seam, so it
	// stays a global.
	framesprite: null as PIXI.Sprite
};
